import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { useState, useEffect } from "react";
import { 
  Radio, 
  User, 
  Music,
  LayoutDashboard,
  Heart,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Ticket,
  FileText,
  Inbox
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { API } from "@/App";

// Bottom Navigation Bar Component - Fixed at bottom on mobile for ALL pages
export const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [inboxCount, setInboxCount] = useState(0);

  // Fetch inbox unread count for artists
  useEffect(() => {
    const fetchInboxCount = async () => {
      if (!user || user.role !== "artist") return;
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API}/inbox/unread/count`);
        setInboxCount(response.data.total_unread || 0);
      } catch (e) {
        console.error("Failed to fetch inbox count:", e);
      }
    };
    
    fetchInboxCount();
    // Poll every 30 seconds
    const interval = setInterval(fetchInboxCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Don't show on auth page, overlay pages, payment pages, or admin dashboard
  if (location.pathname === "/auth" || 
      location.pathname.includes("/overlay") ||
      location.pathname.includes("/payment") ||
      location.pathname.includes("/admin")) {
    return null;
  }

  // Artist navigation with Inbox
  const artistNavItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/artist/dashboard" },
    { icon: Radio, label: "Live", path: "/live" },
    { icon: FileText, label: "Offline", path: "/offline-reviewers" },
    { icon: Inbox, label: "Inbox", path: "/artist/dashboard", tab: "inbox", count: inboxCount },
  ];

  // Reviewer navigation
  const reviewerNavItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/reviewer/dashboard" },
    { icon: Radio, label: "Live", path: "/live" },
    { icon: FileText, label: "Offline", path: "/offline-reviewers" },
  ];

  // Guest navigation (not logged in)
  const guestNavItems = [
    { icon: Radio, label: "Live", path: "/live" },
    { icon: FileText, label: "Offline", path: "/offline-reviewers" },
    { icon: User, label: "Sign In", path: "/auth" },
  ];

  const getNavItems = () => {
    if (!user) return guestNavItems;
    if (user.role === "artist") return artistNavItems;
    if (user.role === "reviewer") return reviewerNavItems;
    return guestNavItems;
  };

  const navItems = getNavItems();

  const isActive = (item) => {
    // Special handling for inbox tab
    if (item.tab === "inbox") {
      return location.pathname === "/artist/dashboard" && location.search.includes("tab=inbox");
    }
    // Dashboard active check
    if (item.path === "/artist/dashboard" && !item.tab) {
      return location.pathname === "/artist/dashboard" && !location.search.includes("tab=inbox");
    }
    if (item.path === "/reviewer/dashboard") {
      return location.pathname === "/reviewer/dashboard";
    }
    return location.pathname === item.path;
  };

  const handleNavClick = (item) => {
    if (item.tab) {
      navigate(`${item.path}?tab=${item.tab}`);
    } else {
      navigate(item.path);
    }
  };

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-[100] bg-slate-900 border-t border-gray-800">
      {/* Navigation buttons */}
      <div className="flex items-center justify-around px-2 py-2">
        {navItems.map((item, index) => (
          <button
            key={item.path + (item.tab || "") + index}
            onClick={() => handleNavClick(item)}
            className={`flex flex-col items-center justify-center py-2 px-3 rounded-xl transition-all relative ${
              isActive(item)
                ? "text-purple-400 bg-purple-500/10"
                : "text-gray-400 hover:text-white"
            }`}
          >
            <div className="relative">
              <item.icon className={`w-5 h-5 ${isActive(item) ? "text-purple-400" : ""}`} />
              {/* Badge for inbox count */}
              {item.count > 0 && (
                <span className="absolute -top-1 -right-2 min-w-[16px] h-4 bg-red-500 rounded-full text-[10px] flex items-center justify-center text-white font-bold px-1">
                  {item.count > 99 ? '99+' : item.count}
                </span>
              )}
            </div>
            <span className="text-[10px] mt-1 font-medium">{item.label}</span>
          </button>
        ))}
      </div>
      {/* Safe area spacer for iOS - fills the white gap */}
      <div className="h-[env(safe-area-inset-bottom)] bg-slate-900" />
    </nav>
  );
};

// Slide-out Mobile Menu Component
export const MobileSlideMenu = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleNavigate = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    logout();
    onClose();
    navigate("/");
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 z-50 md:hidden"
        onClick={onClose}
      />
      
      {/* Slide Menu */}
      <div className="fixed top-0 right-0 h-full w-72 bg-slate-900 border-l border-gray-800 z-50 md:hidden transform transition-transform duration-300 ease-out">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-800">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Music className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-white">Afterplay</span>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User Info */}
          {user && (
            <div className="p-4 border-b border-gray-800">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg">
                  {(user.display_name || user.name)?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-white font-medium">{user.display_name || user.name}</p>
                  <p className="text-sm text-gray-400 capitalize">{user.role}</p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Links */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-1">
              <MenuLink icon={Radio} label="Live" onClick={() => handleNavigate("/live")} badge="Live" />
              <MenuLink icon={FileText} label="Offline" onClick={() => handleNavigate("/offline-reviewers")} />
              
              {user && user.role === "artist" && (
                <>
                  <div className="py-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wider px-3">Artist</p>
                  </div>
                  <MenuLink icon={LayoutDashboard} label="Dashboard" onClick={() => handleNavigate("/artist/dashboard")} />
                  <MenuLink icon={Heart} label="Following" onClick={() => handleNavigate("/artist/dashboard")} />
                  <MenuLink icon={Ticket} label="Support" onClick={() => handleNavigate("/support")} />
                </>
              )}

              {user && user.role === "reviewer" && (
                <>
                  <div className="py-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wider px-3">Reviewer</p>
                  </div>
                  <MenuLink icon={LayoutDashboard} label="Dashboard" onClick={() => handleNavigate("/reviewer/dashboard")} />
                  <MenuLink icon={Ticket} label="Support" onClick={() => handleNavigate("/support")} />
                </>
              )}

              {user && user.role === "admin" && (
                <>
                  <div className="py-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wider px-3">Admin</p>
                  </div>
                  <MenuLink icon={LayoutDashboard} label="Admin Dashboard" onClick={() => handleNavigate("/admin")} />
                  <MenuLink icon={Radio} label="Live Reviews" onClick={() => handleNavigate("/live")} />
                  <MenuLink icon={FileText} label="Offline Reviews" onClick={() => handleNavigate("/offline-reviewers")} />
                  <MenuLink icon={Ticket} label="Tickets" onClick={() => handleNavigate("/admin")} />
                  <MenuLink icon={Settings} label="Settings" onClick={() => handleNavigate("/admin")} />
                </>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-4 border-t border-gray-800">
            {user ? (
              <Button
                variant="ghost"
                className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-500/10"
                onClick={handleLogout}
              >
                <LogOut className="w-5 h-5 mr-3" />
                Log Out
              </Button>
            ) : (
              <Button
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
                onClick={() => handleNavigate("/auth")}
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Menu Link Component
const MenuLink = ({ icon: Icon, label, onClick, badge }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center justify-between px-3 py-3 rounded-xl text-gray-300 hover:text-white hover:bg-slate-800 transition-colors"
  >
    <div className="flex items-center gap-3">
      <Icon className="w-5 h-5" />
      <span className="font-medium">{label}</span>
    </div>
    <div className="flex items-center gap-2">
      {badge && (
        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
          {badge}
        </Badge>
      )}
      <ChevronRight className="w-4 h-4 text-gray-600" />
    </div>
  </button>
);

// Mobile Header Component - Consistent across pages
export const MobileHeader = ({ title, showBack, onBack, rightContent }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <header className="md:hidden fixed top-0 left-0 right-0 z-40 bg-slate-900/95 backdrop-blur-lg border-b border-gray-800">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            {showBack && (
              <button 
                onClick={onBack || (() => navigate(-1))}
                className="p-2 -ml-2 text-gray-400 hover:text-white"
              >
                <ChevronRight className="w-5 h-5 rotate-180" />
              </button>
            )}
            <h1 className="text-lg font-bold text-white truncate">{title}</h1>
          </div>
          <div className="flex items-center gap-2">
            {rightContent}
            <button 
              onClick={() => setMenuOpen(true)}
              className="p-2 text-gray-400 hover:text-white"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>
      <MobileSlideMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
};

// Hook to detect if mobile
export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

export default MobileBottomNav;
